import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { fixEncoding } from "utils/text";
import { useApp } from "../../../context/AppContext";
import { useAuth, hasRol } from "../../../context/AuthContext";
import { getPedido, getPedidoFromServer, getPedidoEntregado, podarPartesVacias } from "../services/pedidosFS";
import { cambiarEstado, ESTADOS } from "../services/estados";
import { getFlag } from "../services/featureFlags";
import QrScanner from "../../../components/QrScanner";
import { getCatalogoByCodUru } from "../services/catalogo";
import { getStockTiras, confirmarPedidoConStock } from "../services/stockTiras";
import { db } from "../../../firebase";
import { doc, updateDoc, serverTimestamp, arrayUnion, getDoc, getDocs, collection, query, where } from "firebase/firestore";

import KitVinculo from "./KitVinculo";
import KitComponentesFijos from "./KitComponentesFijos";
import { esParteOperario, parteDeProducto, esKitProducto, esKitPredefinido, partesConProductos, catalogoCompletoPara, expandirProductosKit, esEncabezadoKit } from "../services/categoriasOverride";
import PackConfirmModal from "./PackConfirmModal";
import PackErrorModal from "./PackErrorModal";
import Modal from "./Modal";
import VolverListaPedidos from "./VolverListaPedidos";

// ============== Helpers ==============
function toURUCode(value) {
  const s = String(value ?? "").trim();
  const mNum = s.match(/\b\d{7,}\b/);
  if (mNum) return mNum[0];
  return s.split(/\s+/)[0].replace(/[^\w-]/g, "");
}
function toCustomerNo(uru) {
  return String(uru || "").trim() || "—";
}
function reqQty(it) {
  return it.cant ?? it.cantidad ?? it.qty ?? 0;
}
function keyFor(it, i) {
  return `${(it.cod || it.codigo || it.codigoURU || "IDX")}-${i}`;
}

// Nombre legible del producto. Prioridad: nombre del CATÁLOGO (importación, customerNo);
// si el catálogo no lo tiene, se usa la descripción del renglón (Finnegans).
// fixEncoding corrige mojibake de Windows-1252 que Finnegans puede enviar mal codificado.
function getNombreProducto(it, cat) {
  const catNombre = cat?.customerNo || cat?.customer_no || "";
  if (catNombre) return fixEncoding(catNombre);
  const desc = it.descripcion || it.desc || it.nombre || it.cod || it.codigo || it.codigoURU || "Producto";
  return fixEncoding(desc);
}

// Lista de productos en modo solo lectura (sin checks ni interacción)
function ListaProductosSoloLectura({ pedido, catalogoMap }) {
  const productos = pedido?.productos || [];
  return (
    <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
      {productos.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 16px", color: "#94a3b8" }}>
          <p style={{ margin: 0 }}>No hay productos en este pedido.</p>
        </div>
      ) : (
        productos.map((it, i) => {
          const k = keyFor(it, i);
          const qty = reqQty(it);
          const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
          const uru = toURUCode(raw);
          const cat = catalogoMap?.[uru] || {};
          const nombre = getNombreProducto(it, cat);
          const color = cat?.finish || cat?.color || "";

          return (
            <div key={k}>
              <div
                style={{
                  display: "flex", alignItems: "center", gap: "14px",
                  width: "100%", textAlign: "left",
                  background: "#fff",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "14px",
                  padding: "14px 16px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontSize: "0.85rem", fontWeight: 600,
                    color: "#1e293b", lineHeight: 1.35, wordBreak: "break-word",
                  }}>
                    {nombre}
                  </p>
                  {color && (
                    <p style={{ margin: "2px 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>{color}</p>
                  )}
                </div>
                <div style={{
                  flexShrink: 0,
                  background: "#f1f5f9", color: "#475569",
                  fontWeight: 700, fontSize: "0.85rem",
                  padding: "4px 10px", borderRadius: "8px",
                }}>
                  ×{qty}
                </div>
              </div>
              {esKitPredefinido(uru) && <KitComponentesFijos codigoKit={uru} cantKit={qty} />}
            </div>
          );
        })
      )}
    </div>
  );
}

// ============== Pantalla ==============
export default function PedidoDetalleOperario() {
  const { id: rawId } = useParams();
  const pedidoId = useMemo(() => decodeURIComponent(rawId || ""), [rawId]);
  const { toast, haptics, confirmarAvance } = useApp();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const puedeVincularKits = ["encargado", "admin"].includes((profile?.rol || profile?.role || "").toLowerCase());
  const esAdmin = hasRol(profile, "admin"); // el admin puede preparar/completar cualquier parte, sin límites

  const [pedido, _setPedido]        = useState(null);
  // Expande los kits vinculados: sus componentes se muestran como productos
  // normales del pedido (categorizados, preparables). Idempotente.
  const setPedido = useCallback((arg) => {
    _setPedido(prev => {
      const next = typeof arg === "function" ? arg(prev) : arg;
      return next ? { ...next, productos: expandirProductosKit(next) } : next;
    });
  }, []);
  const [lite, setLite]             = useState(true);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [autoStarting, setAutoStarting] = useState(false);
  const autoStartedRef = useRef(false);

  const [prep, setPrep]             = useState({});
  const [catalogoMap, setCatalogoMap] = useState({});
  const [checks, setChecks]         = useState({});

  // overlay (solo modo escáner)
  const [scanForKey, setScanForKey]   = useState(null);
  const [packConfirm, setPackConfirm] = useState(null);
  const [packError, setPackError]     = useState(null);

  // problema
  const [showProblema, setShowProblema] = useState(false);
  const [reportando, setReportando]     = useState(false);
  const [problemaForm, setProblemaForm] = useState({ productoIdxs: [], tipo: "FALTA_STOCK", desc: "", cantidades: {} });
  const [editMod, setEditMod] = useState(null);
  const [editVal, setEditVal] = useState("");

  // ===== Carga inicial =====
  useEffect(() => {
    (async () => {
      if (!pedidoId) return;
      setLoading(true);
      try {
        let p = await getPedido(pedidoId);
        if (!p) {
          // Pedidos ya entregados se mueven a 'pedidos_entregados' y se borran de 'pedidos'
          p = await getPedidoEntregado(pedidoId);
        }
        setPedido(p || null);
      } catch (e) {
        console.error("[Operario] error cargando pedido:", e);
        setPedido(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [pedidoId]);

  useEffect(() => { (async () => setLite(await getFlag("MODO_LITE")))(); }, []);

  // Catálogo para enriquecer nombres (best-effort)
  useEffect(() => {
    if (!pedido?.productos || !Array.isArray(pedido.productos)) return;
    const codigosURU = Array.from(new Set(
      pedido.productos
        .map(p => p.cod || p.codigo || p.codigoURU || p.desc || p.descripcion || p.nombre)
        .filter(Boolean).map(toURUCode).filter(Boolean)
    ));
    if (codigosURU.length === 0) { setCatalogoMap({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const entries = await Promise.all(
          codigosURU.map(async (uru) => [uru, (await getCatalogoByCodUru(uru)) || null])
        );
        if (!cancelled) setCatalogoMap(Object.fromEntries(entries));
      } catch (e) { console.error("[Operario] catálogo error", e?.code || e); }
    })();
    return () => { cancelled = true; };
  }, [pedido?.productos]);

  // Auto-poda de categorías vacías: si una parte del pedido quedó sin productos
  // (p. ej. Ventas eliminó en Finnegans el único producto de esa categoría), se
  // saca de asignaciones para que no aparezca ni bloquee la finalización.
  const pruneRanRef = useRef(false);
  useEffect(() => {
    if (pruneRanRef.current) return;
    if (!pedido) return;
    if (![ESTADOS.EN_PREPARACION, ESTADOS.ASIGNADO].includes(pedido.estado)) return;
    const asg = pedido.asignaciones;
    if (!asg || Object.keys(asg).length === 0) return;
    // Solo podamos cuando el catálogo está cargado para TODOS los productos (clasificación confiable).
    if (!catalogoCompletoPara(pedido.productos, catalogoMap)) return;
    const conProd = partesConProductos(pedido.productos, catalogoMap);
    const vacias = Object.keys(asg).filter(p => !conProd.has(p));
    if (vacias.length === 0) return;
    pruneRanRef.current = true;
    (async () => {
      try {
        await podarPartesVacias(pedido.id || pedidoId, vacias);
        const p = await getPedido(pedidoId);
        setPedido(p);
      } catch (e) {
        console.error("[Operario] podar partes vacías:", e);
        pruneRanRef.current = false;
      }
    })();
  }, [pedido, catalogoMap, pedidoId]);

  // Stock para modo escáner
  useEffect(() => {
    if (lite) return; // no hace falta en modo lite
    let cancel = false;
    (async () => {
      if (!pedido?.productos) return;
      const next = {};
      for (let i = 0; i < pedido.productos.length; i++) {
        const it = pedido.productos[i];
        const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
        const uru = toURUCode(raw);
        if (!uru) continue;
        const stock = await getStockTiras(uru).catch(() => 0);
        next[keyFor(it, i)] = { stock, usarSueltas: 0, paquete: null, usarDePaquete: 0 };
      }
      if (!cancel) setPrep(next);
    })();
    return () => { cancel = true; };
  }, [pedido?.productos, lite]);

  // ¿El usuario actual es responsable de este pedido (por uid o por override de parte)?
  const soyResponsable = useMemo(() => {
    if (!pedido) return false;
    if (pedido.operarioId && pedido.operarioId === user?.uid) return true;
    if ((pedido.responsablesUids || []).includes(user?.uid)) return true;
    return false;
  }, [pedido, user?.uid]);

  // Auto-transición ASIGNADO → EN_PREPARACION
  useEffect(() => {
    if (!pedido || !user?.uid) return;
    if (pedido.estado !== ESTADOS.ASIGNADO) return;
    // Cualquier responsable (operario primario, segundo responsable o encargado que
    // abre su parte) puede iniciar la preparación del pedido.
    if (!soyResponsable) return;
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    setAutoStarting(true);
    (async () => {
      try {
        await cambiarEstado(pedidoId, ESTADOS.EN_PREPARACION);
        const p = await getPedido(pedidoId);
        setPedido(p);
      } catch (e) {
        console.error("[Operario] auto-transition error:", e);
        toast?.error?.("No se pudo iniciar la preparación. Intentá de nuevo.");
        autoStartedRef.current = false;
      } finally {
        setAutoStarting(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido?.estado, pedido?.operarioId, user?.uid, soyResponsable]);

  // ¿Es ISABELA? El operario de ISABELA finaliza en CONTROLADO (sin paso de control del encargado)
  const esIsabela = (pedido?.deposito || "").trim().toUpperCase() === "ISABELA";

  // Estado del pedido al momento de abrir esta pantalla (para distinguir "recién
  // terminé la preparación" de "estoy revisando un pedido ya controlado").
  const initialEstadoRef = useRef(undefined);
  useEffect(() => {
    if (pedido && initialEstadoRef.current === undefined) {
      initialEstadoRef.current = pedido.estado;
    }
  }, [pedido]);

  // Redirect al completarse (solo si la transición ocurrió en esta sesión,
  // no si el operario está reabriendo un pedido que ya estaba CONTROLADO/PREPARADO).
  useEffect(() => {
    const estadoFinal = esIsabela ? ESTADOS.CONTROLADO : ESTADOS.PREPARADO;
    if (pedido?.estado === estadoFinal && initialEstadoRef.current !== estadoFinal) {
      initialEstadoRef.current = estadoFinal; // evitar re-disparo
      confirmarAvance?.({
        titulo: esIsabela ? "Pedido controlado" : "Pedido preparado",
        mensaje: `#${pedido?.numero || pedido?.id || ""} listo`,
        onOk: () => navigate("/pedidos"),
      });
    }
  }, [pedido?.estado, esIsabela, navigate, confirmarAvance]);

  // ===== Partes del usuario según asignaciones =====
  // [] = pedido viejo sin asignaciones o encargado/admin viendo → muestra todo.
  // Las partes se derivan SOLO del uid. Un mismo operario puede tener VARIAS
  // categorías del mismo pedido (p. ej. PVC y VID a Luis): las ve TODAS juntas.
  const misPartes = useMemo(() => {
    const asg = pedido?.asignaciones;
    if (!asg) return [];
    return Object.keys(asg).filter((p) => asg[p]?.uid && asg[p].uid === user?.uid);
  }, [pedido?.asignaciones, user?.uid]);
  const tengoParte = misPartes.length > 0;

  // ===== LITE: productos filtrados por las partes del usuario (todas juntas) =====
  const productosLite = useMemo(() => {
    // El encabezado del kit no se prepara (sus componentes ya vienen expandidos).
    const all = (pedido?.productos || []).filter(it => !esEncabezadoKit(it));
    if (!tengoParte) return all;
    // Partes que SÍ tienen responsable asignado.
    const partesAsignadas = Object.keys(pedido?.asignaciones || {});
    return all.filter(it => {
      const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
      const uru = toURUCode(raw);
      const parte = parteDeProducto(uru, catalogoMap?.[uru], it);
      // Muestro los productos de MIS partes, PERO TAMBIÉN los "huérfanos": los que
      // caen en una parte que no está asignada a nadie (p. ej. un producto sin
      // catálogo que terminó en "Otros", o una clasificación que cambió después de
      // asignar). Regla: un producto NUNCA debe quedar oculto en preparación.
      return misPartes.includes(parte) || !partesAsignadas.includes(parte);
    });
  }, [pedido?.productos, misPartes, tengoParte, catalogoMap, pedido?.asignaciones]);
  // ===== Componentes de cada kit (del pedido vinculado) → se listan como ítems tildables =====
  const [kitComps, setKitComps] = useState({}); // { [kitUru]: { pedidoId, numero, comps: [] } }
  useEffect(() => {
    const kits = (pedido?.productos || []).map((it) => {
      const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
      const uru = toURUCode(raw);
      if (!esKitProducto(uru, catalogoMap?.[uru], it)) return null;
      // Los kits con componentes YA explotados (snapshot en kitComponentes) se
      // muestran como productos normales → no usar el sistema viejo de kitComps.
      if (pedido?.kitComponentes && pedido.kitComponentes[uru]) return null;
      return { uru, descripcion: it.descripcion || it.desc || it.nombre };
    }).filter(Boolean);
    if (kits.length === 0) { setKitComps({}); return; }
    let cancel = false;
    (async () => {
      const result = {};
      let compCandidatos = null; // cache de pedidos del depósito para el camino 2
      const textoDesc = (p) => [p.descripcion, p.observaciones, p.nota, p.comentario, p.detalle].filter(Boolean).join(" ").toUpperCase();
      for (const kt of kits) {
        let compId = pedido?.kitsVinculados?.[kt.uru]?.pedidoId || null;
        // Camino 1: número (4-6 dígitos) dentro de la descripción del kit.
        if (!compId && kt.descripcion) {
          const nums = String(kt.descripcion).match(/\d{4,6}/g) || [];
          for (const n of nums) {
            for (const valor of [Number(n), n]) {
              try {
                const snap = await getDocs(query(collection(db, "pedidos"), where("numero", "==", valor), where("deposito", "==", pedido.deposito || "R8")));
                const found = snap.docs.find(d => d.id !== pedido.id);
                if (found) { compId = found.id; break; }
              } catch (e) { console.error("[kit comp]", e); }
            }
            if (compId) break;
          }
        }
        // Camino 2: pedido con "COMPONENTES KIT" que menciona el N° de ESTE pedido.
        if (!compId) {
          const miNumero = String(pedido?.numero || "").trim();
          if (miNumero) {
            try {
              if (compCandidatos === null) {
                const snap = await getDocs(query(collection(db, "pedidos"), where("deposito", "==", pedido.deposito || "R8")));
                compCandidatos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
              }
              const re = new RegExp(`\\b${miNumero}\\b`);
              const comp = compCandidatos.find(p => p.id !== pedido.id && textoDesc(p).includes("COMPONENTES KIT") && re.test(textoDesc(p)));
              if (comp) compId = comp.id;
            } catch (e) { console.error("[kit comp 2]", e); }
          }
        }
        if (compId) {
          try {
            const snap = await getDoc(doc(db, "pedidos", compId));
            if (snap.exists()) result[kt.uru] = { pedidoId: compId, numero: snap.data().numero, comps: snap.data().productos || [] };
          } catch (e) { console.error("[kit comp load]", e); }
        }
      }
      if (!cancel) setKitComps(result);
    })();
    return () => { cancel = true; };
  }, [pedido?.productos, pedido?.kitsVinculados, pedido?.deposito, pedido?.id, pedido?.numero, catalogoMap]);

  // Cargar el catálogo (Customer No) de los códigos de los componentes de kit,
  // que vienen de otro pedido y no estaban en el catálogo inicial.
  useEffect(() => {
    const faltantes = [];
    Object.values(kitComps).forEach(kc => (kc.comps || []).forEach(c => {
      const uru = toURUCode(c.cod || c.codigo || c.codigoURU || c.desc || c.descripcion || c.nombre || "");
      if (uru && !(uru in catalogoMap)) faltantes.push(uru);
    }));
    const unicos = [...new Set(faltantes)];
    if (unicos.length === 0) return; // ya están todos → no hace nada (evita loops)
    let cancel = false;
    (async () => {
      try {
        const entries = await Promise.all(unicos.map(async uru => [uru, (await getCatalogoByCodUru(uru)) || null]));
        if (!cancel) setCatalogoMap(prev => ({ ...prev, ...Object.fromEntries(entries) }));
      } catch (e) { console.error("[kit comp catálogo]", e); }
    })();
    return () => { cancel = true; };
  }, [kitComps, catalogoMap]);

  // Lista final de ítems a cargar: cada kit se REEMPLAZA por sus componentes.
  const itemsCargables = useMemo(() => {
    const out = [];
    productosLite.forEach((it, i) => {
      const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
      const uru = toURUCode(raw);
      const esK = esKitProducto(uru, catalogoMap?.[uru], it);
      if (esK && kitComps[uru]?.comps?.length) {
        const kitNombre = getNombreProducto(it, catalogoMap?.[uru]);
        kitComps[uru].comps.forEach((c, ci) => {
          out.push({ ...c, _key: `kit:${uru}:${ci}`, _kitUru: uru, _kitNombre: kitNombre, _kitNumero: kitComps[uru].numero });
        });
      } else {
        out.push({ ...it, _key: keyFor(it, i) });
      }
    });
    return out;
  }, [productosLite, kitComps, catalogoMap]);

  const totalProductos = itemsCargables.length;
  const checkedCount = itemsCargables.filter(it => !!checks[it._key]).length;
  const allChecked = totalProductos > 0 && checkedCount === totalProductos;
  const progreso = totalProductos > 0 ? Math.round((checkedCount / totalProductos) * 100) : 0;

  // ===== Modificaciones detectadas por el sync de Finnegans =====
  const modInfo = pedido?.modificacionInfo;
  const addedSet = useMemo(
    () => new Set((modInfo?.addedItems || []).map((x) => x.cod)),
    [modInfo]
  );
  const changedMap = useMemo(() => {
    const m = {};
    (modInfo?.changedItems || []).forEach((x) => { m[x.cod] = x; });
    return m;
  }, [modInfo]);
  function getModBadge(uru) {
    if (addedSet.has(uru)) {
      return { label: "NUEVO", bg: "#dcfce7", color: "#15803d" };
    }
    const chg = changedMap[uru];
    if (chg) {
      const diff = chg.cantAhora - chg.cantAntes;
      return diff > 0
        ? { label: `+${diff} más`, bg: "#fef3c7", color: "#b45309" }
        : { label: `${diff} menos`, bg: "#fee2e2", color: "#dc2626" };
    }
    return null;
  }

  // ===== Escáner: productos filtrados por categoría (perfiles + cortinas + kits) =====
  const productosOperario = useMemo(() => {
    return (pedido?.productos || []).filter((it) => {
      const raw = it.cod || it.descripcion || it.desc || "";
      const uru = toURUCode(raw);
      return esParteOperario(uru, catalogoMap?.[uru]);
    });
  }, [pedido?.productos, catalogoMap]);

  // ===== Helpers escáner =====
  function getItemInfoByKey(k) {
    if (!pedido?.productos) return null;
    const idx = Number(String(k).split("-").pop());
    const it = pedido.productos[idx];
    if (!it) return null;
    const need = reqQty(it);
    const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
    const uruExpected = toURUCode(raw);
    return { it, idx, need, uruExpected };
  }

  function getRemainingForKey(k) {
    const info = getItemInfoByKey(k);
    if (!info) return 0;
    const p = prep[k] || {};
    return Math.max(0, info.need - Number(p.usarSueltas || 0) - Number(p.usarDePaquete || 0));
  }

  function clampPrepForNeed(k, draft) {
    const p = { ...(prep[k] || {}), ...draft };
    const info = getItemInfoByKey(k);
    if (!info) return p;
    const { need } = info;
    const stock = Number(p.stock || 0);
    const packSize = Number(p.paquete?.packSize || 0);
    let usarSueltas = Math.min(Number(p.usarSueltas || 0), stock);
    let usarDePaquete = Math.min(Number(p.usarDePaquete || 0), packSize);
    const total = usarSueltas + usarDePaquete;
    if (total > need) {
      const exceso = total - need;
      if (usarDePaquete >= exceso) usarDePaquete -= exceso;
      else usarSueltas = Math.max(0, usarSueltas - (exceso - usarDePaquete));
    }
    return { ...p, usarSueltas, usarDePaquete };
  }

  function onScanPaquete(payload) {
    try {
      if (!scanForKey) return;
      const info = getItemInfoByKey(scanForKey);
      if (!info) { toast?.error?.("No se encontró el producto seleccionado"); return; }
      const { uruExpected } = info;
      const codUruQR = toURUCode(payload.codUru || payload.codigo_urualum || payload.codigo || "");
      const packSize = Number(payload.packSize || payload.cantidad || 0);
      if (!codUruQR || !packSize) { toast?.error?.("QR inválido: faltan datos"); return; }
      if (codUruQR !== uruExpected) {
        setPackError({ esperado: uruExpected, qr: codUruQR });
        return;
      }
      const remaining = getRemainingForKey(scanForKey);
      const defaultQty = Math.min(remaining, packSize);
      const nombre = catalogoMap?.[uruExpected]?.customerNo || toCustomerNo(uruExpected);
      setPackConfirm({ key: scanForKey, nombre, packSize, remaining, defaultQty });
      setScanForKey(null);
    } catch (e) {
      console.error(e);
      toast?.error?.("No se pudo procesar el QR");
    }
  }

  // ===== Acciones =====
  function toggleCheck(k) { setChecks(prev => ({ ...prev, [k]: !prev[k] })); }

  function marcarTodo() {
    const next = {};
    itemsCargables.forEach((it) => { next[it._key] = true; });
    setChecks(next);
  }

  // ¿Es un pedido dividido donde YO soy responsable de TODAS las partes? (encargado
  // que se autoasignó todo el pedido). En ese caso, si hay error, no hace falta que
  // el encargado se autocontrole: va directo a ERROR_CONFIRMADO (lo toma Ventas).
  function soyUnicoResponsable(partesPresentes) {
    if (!puedeVincularKits) return false;
    if (partesPresentes.length > 0) return misPartes.length === partesPresentes.length;
    return pedido?.operarioId === user?.uid; // pedido clásico (sin partes) autoasignado
  }

  // Escala el error al estado correspondiente, juntando los problemas de TODAS las
  // partes (multi-categoría) para que Ventas/encargado los vean de una sola vez.
  async function escalarErrorAgregado(conErrorMap, partesPreparadas) {
    const agregado = Object.values(conErrorMap).flatMap(x => x.errorProductos || []);
    const detalle = agregado
      .map((p) => `• ${p.nombre}: ${p.tipo === "sin_stock" ? "Falta de stock" : "Otro motivo"}${p.cantReal !== undefined ? ` (stock real: ${p.cantReal})` : ""}${p.descripcion ? ` — ${p.descripcion}` : ""}`)
      .join("\n");
    const partesPresentes = Object.keys(pedido?.asignaciones || {});
    const estadoError = (!esIsabela && soyUnicoResponsable(partesPresentes)) ? ESTADOS.ERROR_CONFIRMADO : ESTADOS.CON_ERROR;
    await cambiarEstado(pedido.id || pedidoId, estadoError, {
      partesPreparadas,
      partesConError: conErrorMap,
      errorProductos: agregado,
      errorDetalle: detalle,
      errorProductoCod: agregado[0]?.cod || null,
      errorProductoNombre: agregado.map((p) => p.nombre).join(", "),
    });
    haptics?.success?.();
    const p = await getPedido(pedidoId);
    setPedido(p);
    confirmarAvance?.({
      titulo: "Error reportado",
      mensaje: `#${pedido?.numero || pedido?.id || ""} — ${estadoError === ESTADOS.ERROR_CONFIRMADO ? "enviado a Ventas" : "lo revisa el encargado"}`,
      onOk: () => navigate(puedeVincularKits ? "/pedidos" : "/operario/asignados"),
    });
  }

  async function finalizarPreparacion() {
    if (saving) return;
    try {
      setSaving(true);
      const asg = pedido?.asignaciones;
      const partesPresentes = asg ? Object.keys(asg) : [];

      // ── Flujo por partes (R8): marca TODAS mis partes; el pedido pasa a
      //    PREPARADO (o escala el error) solo cuando TODAS las partes terminaron. ──
      if (!esIsabela && tengoParte && partesPresentes.length > 0) {
        const ref = doc(db, "pedidos", pedido.id || pedidoId);

        // 1) Marcar MIS partes con dot-notation → MERGE seguro: no pisa las partes
        //    que otro responsable (o el encargado) haya terminado mientras tanto.
        const upd = {};
        misPartes.forEach(p => { upd[`partesPreparadas.${p}`] = true; });
        await updateDoc(ref, upd);

        // 2) Releer FRESCO DESDE EL SERVIDOR para decidir si ya están TODAS las
        //    partes terminadas. Debe saltear el caché: si el otro operario acaba de
        //    marcar su parte, una lectura cacheada no la vería y el pedido quedaría
        //    trabado en EN_PREPARACION pese a estar 100% listo.
        const fresh = await getPedidoFromServer(pedidoId);
        const prep = fresh?.partesPreparadas || {};
        const conError = fresh?.partesConError || {};
        const asgFresh = fresh?.asignaciones || asg || {};
        const presentesFresh = Object.keys(asgFresh);
        const terminada = (p) => prep[p] === true || conError[p];
        const todasTerminadas = presentesFresh.length > 0 && presentesFresh.every(terminada);

        if (todasTerminadas) {
          // Si alguna parte tuvo problema → escalamos con TODOS los problemas juntos.
          if (presentesFresh.some(p => conError[p])) {
            await escalarErrorAgregado(conError, prep);
            return;
          }
          // Todas OK → PREPARADO (las partes ya quedaron escritas arriba).
          await cambiarEstado(pedidoId, ESTADOS.PREPARADO);
          haptics?.success?.();
          toast?.success?.("¡Pedido preparado! (todas las partes listas)");
          setPedido(fresh);
          return;
        }

        // Falta al menos otra parte: mi parte ya quedó registrada, vuelvo a la lista.
        haptics?.success?.();
        setPedido(fresh);
        confirmarAvance?.({
          titulo: "¡Tu parte quedó lista!",
          mensaje: `Preparaste tu parte del pedido #${pedido?.numero || pedido?.id || ""}. Falta que se terminen las demás partes.`,
          onOk: () => navigate(puedeVincularKits ? "/pedidos" : "/operario/asignados"),
        });
        return;
      }

      // ── Flujo clásico (ISABELA, o pedidos sin asignaciones por parte) ──
      await updateDoc(doc(db, "pedidos", pedido.id || pedidoId), { prepPerfilesOk: true });
      const estadoDestino = esIsabela ? ESTADOS.CONTROLADO : ESTADOS.PREPARADO;
      await cambiarEstado(pedidoId, estadoDestino);
      haptics?.success?.();
      toast?.success?.("¡Pedido preparado!");
      const p = await getPedido(pedidoId);
      setPedido(p);
    } catch (e) {
      console.error(e);
      toast?.error?.(e.message || "No se pudo confirmar la preparación");
    } finally {
      setSaving(false);
    }
  }

  // ── OPCIÓN 2: el operario cargó lo que había (no frena el pedido). Registra la
  //    modificación por producto (cantPedida vs cantReal) y el pedido SIGUE su curso
  //    normal; el encargado la confirma al controlar. No cambia de estado. ──
  // Editar la cantidad cargada de una modificación (si el responsable se equivocó).
  async function editarModificacionOperario(cod, nuevaRaw) {
    const mods = Array.isArray(pedido?.modificaciones) ? pedido.modificaciones.slice() : [];
    let idx = -1;
    for (let k = mods.length - 1; k >= 0; k--) { if (String(mods[k]?.cod || "").trim() === String(cod).trim()) { idx = k; break; } }
    if (idx < 0) return false;
    const cantPed = mods[idx].cantPedida;
    const nueva = Number(nuevaRaw);
    if (String(nuevaRaw).trim() === "" || isNaN(nueva) || nueva < 0) { toast?.error?.("Cantidad inválida."); return false; }
    if (cantPed != null && cantPed > 0 && nueva >= cantPed) { toast?.error?.(`Tiene que ser MENOR a la pedida (${cantPed}).`); return false; }
    mods[idx] = { ...mods[idx], cantReal: nueva, editadoAt: Date.now() };
    try {
      await updateDoc(doc(db, "pedidos", pedido.id || pedidoId), { modificaciones: mods });
      const fresh = await getPedido(pedido.id || pedidoId); if (fresh) setPedido(fresh);
      toast?.success?.("Cantidad actualizada.");
      return true;
    } catch (e) { console.error(e); toast?.error?.("No se pudo editar la cantidad."); return false; }
  }

  async function registrarModificacion() {
    if (reportando) return;
    const idxs = problemaForm.productoIdxs || [];
    if (idxs.length === 0) { toast?.error?.("Marcá al menos un producto modificado."); return; }
    try {
      setReportando(true);
      const esFaltaStock = problemaForm.tipo === "FALTA_STOCK";
      const desc = problemaForm.desc.trim();
      const productos = pedido?.productos || [];

      // "Seguir" exige Falta de stock + cantidad cargada por producto, SIEMPRE menor a la pedida.
      if (!esFaltaStock) { toast?.error?.("Para 'Seguir' marcá 'Falta de stock' e indicá cuánto cargaste."); setReportando(false); return; }
      for (const i of idxs) {
        const v = problemaForm.cantidades?.[i];
        const cantReal = Number(v);
        const cantPed = Number(productos[i]?.cant ?? productos[i]?.cantidad ?? 0);
        if (v === undefined || v === "" || isNaN(cantReal) || cantReal < 0) {
          toast?.error?.("Indicá cuánto cargaste de cada producto (puede ser 0)."); setReportando(false); return;
        }
        if (cantPed > 0 && cantReal >= cantPed) {
          toast?.error?.(`La cantidad cargada tiene que ser MENOR a la pedida (${cantPed}).`); setReportando(false); return;
        }
      }

      const mods = idxs.map((i) => {
        const it = productos[i];
        const raw = it ? (it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "") : "";
        const uru = toURUCode(raw);
        const cat = catalogoMap[uru] || {};
        const base = it ? getNombreProducto(it, cat) : "Producto desconocido";
        const color = cat?.finish || cat?.color || "";
        const nombre = color ? `${base} (${color})` : base;
        const cantPedida = Number(it?.cant ?? it?.cantidad ?? 0) || null;
        return {
          cod: it?.cod || it?.codigo || null,
          nombre,
          color: color || null,
          motivo: esFaltaStock ? "sin_stock" : "otro",
          cantPedida,
          ...(esFaltaStock ? { cantReal: Number(problemaForm.cantidades?.[i]) } : {}),
          descripcion: desc || null,
          por: user?.uid || null,
          porNombre: profile?.nombre || null,
          at: Date.now(),
        };
      });

      await updateDoc(doc(db, "pedidos", pedido.id || pedidoId), {
        modificaciones: arrayUnion(...mods),
        tieneModificacion: true,
        modificacionEstado: "PENDIENTE_CONTROL",
        modificacionAt: serverTimestamp(),
      });

      setShowProblema(false);
      haptics?.success?.();
      const fresh = await getPedido(pedido.id || pedidoId);
      setPedido(fresh);
      toast?.success?.("Modificación registrada. Seguí preparando el pedido.");
    } catch (e) {
      console.error(e);
      toast?.error?.("No se pudo registrar la modificación.");
    } finally {
      setReportando(false);
    }
  }

  async function reportarProblema() {
    if (reportando) return;
    const idxs = problemaForm.productoIdxs || [];
    if (idxs.length === 0) { toast?.error?.("Marcá al menos un producto con problema."); return; }
    try {
      setReportando(true);
      const esFaltaStock = problemaForm.tipo === "FALTA_STOCK";
      const desc = problemaForm.desc.trim();
      const productos = pedido?.productos || [];

      // Falta de stock → exigir la cantidad de stock real de cada producto marcado.
      if (esFaltaStock) {
        const sinCantidad = idxs.some((i) => {
          const v = problemaForm.cantidades?.[i];
          return v === undefined || v === "" || isNaN(Number(v)) || Number(v) < 0;
        });
        if (sinCantidad) {
          toast?.error?.("Indicá el stock real disponible de cada producto (puede ser 0).");
          return;
        }
      }

      // Un renglón de error por cada producto marcado.
      const errorProductos = idxs.map((i) => {
        const it = productos[i];
        const raw = it ? (it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "") : "";
        const uru = toURUCode(raw);
        const cat = catalogoMap[uru] || {};
        const base = it ? getNombreProducto(it, cat) : "Producto desconocido";
        const color = cat?.finish || cat?.color || "";
        // Incluimos el color en el nombre para distinguir el mismo producto en distintos colores.
        const nombre = color ? `${base} (${color})` : base;
        return {
          cod: it?.cod || it?.codigo || null,
          nombre,
          color: color || null,
          tipo: esFaltaStock ? "sin_stock" : "otro",
          descripcion: desc || null,
          // Stock real disponible informado por el operario (solo falta de stock).
          ...(esFaltaStock ? { cantReal: Number(problemaForm.cantidades?.[i]) } : {}),
        };
      });

      const asg = pedido?.asignaciones;
      const partesPresentes = asg ? Object.keys(asg) : [];

      // ── Multi-parte: guardamos el problema de MIS partes. NO se escala todavía;
      //    recién cuando TODAS las partes terminen (preparadas o con problema) se
      //    manda el pedido a error con los problemas de todas juntas. ──
      if (!esIsabela && tengoParte && partesPresentes.length > 0) {
        const ref = doc(db, "pedidos", pedido.id || pedidoId);

        // Marcar el problema de MIS partes con dot-notation → MERGE seguro: no pisa
        // lo que otro responsable/encargado haya preparado o reportado mientras tanto.
        const upd = {};
        misPartes.forEach(p => { upd[`partesConError.${p}`] = { errorProductos, uid: user?.uid || null, nombre: profile?.nombre || null }; });
        await updateDoc(ref, upd);

        setShowProblema(false);

        // Releer FRESCO DESDE EL SERVIDOR (saltea caché) para decidir si ya están
        // todas las partes terminadas — ver nota en finalizarPreparacion.
        const fresh = await getPedidoFromServer(pedidoId);
        const prep = fresh?.partesPreparadas || {};
        const conError = fresh?.partesConError || {};
        const presentesFresh = Object.keys(fresh?.asignaciones || asg || {});
        const terminada = (p) => prep[p] === true || conError[p];
        const todasTerminadas = presentesFresh.length > 0 && presentesFresh.every(terminada);

        if (!todasTerminadas) {
          haptics?.success?.();
          setPedido(fresh);
          confirmarAvance?.({
            titulo: "Problema registrado",
            mensaje: `Reportaste el problema de tu parte del #${pedido?.numero || pedido?.id || ""}. Se enviará cuando las demás partes terminen.`,
            onOk: () => navigate(puedeVincularKits ? "/pedidos" : "/operario/asignados"),
          });
          return;
        }
        // Todas las partes terminaron → escalamos con todo junto (datos frescos).
        await escalarErrorAgregado(conError, prep);
        return;
      }

      // ── Clásico (ISABELA o pedidos sin partes por categoría) ──
      const detalle = errorProductos
        .map((p) => `• ${p.nombre}: ${esFaltaStock ? "Falta de stock" : "Otro motivo"}${p.cantReal !== undefined ? ` (stock real: ${p.cantReal})` : ""}${desc ? ` — ${desc}` : ""}`)
        .join("\n");
      const estadoError = (!esIsabela && soyUnicoResponsable(partesPresentes)) ? ESTADOS.ERROR_CONFIRMADO : ESTADOS.CON_ERROR;
      await cambiarEstado(pedido.id || pedidoId, estadoError, {
        errorDetalle: detalle,
        errorProductos,
        errorProductoCod: errorProductos[0]?.cod || null,
        errorProductoNombre: errorProductos.map((p) => p.nombre).join(", "),
      });

      setShowProblema(false);
      haptics?.success?.();
      confirmarAvance?.({
        titulo: "Error reportado",
        mensaje: `#${pedido?.numero || pedido?.id || ""} — ${estadoError === ESTADOS.ERROR_CONFIRMADO ? "enviado a Ventas" : "el encargado lo revisará"}`,
        onOk: () => navigate("/pedidos"),
      });
    } catch (e) {
      console.error(e);
      toast?.error?.("No se pudo reportar. Intentá de nuevo.");
    } finally {
      setReportando(false);
    }
  }

  // ===== Estados de carga/acceso =====
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <p style={{ color: "#94a3b8", fontSize: "0.9rem" }}>Cargando pedido…</p>
    </div>
  );
  if (!pedido) return (
    <div style={{ padding: "24px", textAlign: "center" }}>
      <p style={{ color: "#ef4444" }}>Pedido no encontrado.</p>
    </div>
  );
  // Acceso: el responsable primario, cualquier segundo responsable, o el encargado/admin.
  if (pedido.operarioId && !soyResponsable && !puedeVincularKits) return (
    <div style={{ padding: "24px", textAlign: "center" }}>
      <p style={{ color: "#ef4444" }}>No tenés acceso a este pedido.</p>
    </div>
  );

  // Pedido dividido por partes (R8) donde el usuario no es responsable de ninguna:
  // no debe preparar nada acá (p. ej. el encargado abriendo la parte de otro operario)
  // → lo derivamos al detalle de gestión, donde puede ver el responsable y reasignar.
  const tieneAsignaciones = pedido.asignaciones && Object.keys(pedido.asignaciones).length > 0;
  // El admin NO se redirige: puede preparar/completar el pedido aunque no sea su parte.
  if (!esIsabela && tieneAsignaciones && !tengoParte && !esAdmin) {
    return <Navigate to={`/pedidos/${encodeURIComponent(pedido.id || pedidoId)}`} replace />;
  }

  // ===== RENDER =====
  const overlayOpen = !!(scanForKey || packError || packConfirm);

  // Estado: pedido ya entregado (vive en 'pedidos_entregados') — solo lectura
  if (pedido.estado === "ENTREGADO") {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px" }}>
          <VolverListaPedidos to="/pedidos" />
          <div style={{ marginTop: "10px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
            <div>
              <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Pedido #{pedido.numero}
              </p>
              <h1 style={{ margin: "2px 0 0", fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                {pedido.cliente}
              </h1>
            </div>
            <span style={{
              flexShrink: 0,
              background: "#f0fdf4", color: "#15803d",
              fontSize: "0.7rem", fontWeight: 700,
              padding: "3px 10px", borderRadius: "999px",
              letterSpacing: "0.04em", marginTop: "4px",
              border: "1px solid #86efac",
            }}>
              ☑️ ENTREGADO
            </span>
          </div>
          {(pedido.metodoPedido || pedido.deposito) && (
            <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
              {[pedido.metodoPedido, pedido.deposito].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>

        <div style={{ padding: "16px" }}>
          <div style={{
            background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "12px",
            padding: "14px 16px", marginBottom: "16px", textAlign: "center",
          }}>
            <p style={{ margin: 0, fontWeight: 700, color: "#15803d", fontSize: "0.95rem" }}>
              Pedido entregado
            </p>
          </div>
        </div>

        <ListaProductosSoloLectura pedido={pedido} catalogoMap={catalogoMap} />
      </div>
    );
  }

  // Estado: pedido completado (R8) — solo si la transición ocurrió recién
  if (pedido.estado === ESTADOS.PREPARADO && initialEstadoRef.current !== ESTADOS.PREPARADO) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: "80vh", padding: "24px", textAlign: "center", gap: "16px"
      }}>
        <div style={{ fontSize: "4rem", lineHeight: 1 }}>✅</div>
        <h2 style={{ fontWeight: 700, fontSize: "1.4rem", margin: 0 }}>¡Pedido preparado!</h2>
        <p style={{ color: "#64748b", margin: 0 }}>Volviendo a tus pedidos…</p>
      </div>
    );
  }

  // Estado: ISABELA acaba de finalizar la preparación (CONTROLADO recién alcanzado)
  if (pedido.estado === ESTADOS.CONTROLADO && esIsabela && initialEstadoRef.current !== ESTADOS.CONTROLADO) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: "80vh", padding: "24px", textAlign: "center", gap: "16px"
      }}>
        <div style={{ fontSize: "4rem", lineHeight: 1 }}>✅</div>
        <h2 style={{ fontWeight: 700, fontSize: "1.4rem", margin: 0 }}>¡Pedido controlado!</h2>
        <p style={{ color: "#64748b", margin: 0 }}>Volviendo a tus pedidos…</p>
      </div>
    );
  }

  // Estado: ISABELA — pedido ya controlado, esperando que ventas lo despache (solo lectura)
  if (pedido.estado === ESTADOS.CONTROLADO && esIsabela) {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px" }}>
          <VolverListaPedidos to="/pedidos" />
          <div style={{ marginTop: "10px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
            <div>
              <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Pedido #{pedido.numero}
              </p>
              <h1 style={{ margin: "2px 0 0", fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                {pedido.cliente}
              </h1>
            </div>
            <span style={{
              flexShrink: 0,
              background: "#f0fdf4", color: "#15803d",
              fontSize: "0.7rem", fontWeight: 700,
              padding: "3px 10px", borderRadius: "999px",
              letterSpacing: "0.04em", marginTop: "4px",
              border: "1px solid #86efac",
            }}>
              ✅ LISTO
            </span>
          </div>
          {(pedido.metodoPedido || pedido.deposito) && (
            <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
              {[pedido.metodoPedido, pedido.deposito].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>

        <div style={{ padding: "16px" }}>
          <div style={{
            background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "12px",
            padding: "14px 16px", marginBottom: "16px", textAlign: "center",
          }}>
            <p style={{ margin: 0, fontWeight: 700, color: "#15803d", fontSize: "0.95rem" }}>
              Esperando despacho
            </p>
            <p style={{ margin: "4px 0 0", color: "#166534", fontSize: "0.8rem" }}>
              Este pedido ya fue controlado. Ventas se encarga del despacho.
            </p>
          </div>
        </div>

        <ListaProductosSoloLectura pedido={pedido} catalogoMap={catalogoMap} />
      </div>
    );
  }

  // Estado: iniciando
  if (pedido.estado === ESTADOS.ASIGNADO || autoStarting) {
    const met = pedido.metodoEntrega && ({
      AGENCIA: { bg: "#eff6ff", color: "#1d4ed8", icon: "🏢" },
      RETIRA:  { bg: "#f0fdf4", color: "#15803d", icon: "🤝" },
      CAMION:  { bg: "#fef3c7", color: "#92400e", icon: "🚚" },
      GIRA:    { bg: "#fdf4ff", color: "#7e22ce", icon: "🚌" },
    }[pedido.metodoEntrega] || { bg: "#f1f5f9", color: "#475569", icon: "📦" });
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: "80vh", padding: "24px", textAlign: "center", gap: "10px"
      }}>
        <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>
          Pedido #{pedido.numero || pedido.id}
        </p>
        <h1 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" }}>{pedido.cliente || "—"}</h1>
        {met && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            background: met.bg, color: met.color,
            fontSize: "0.85rem", fontWeight: 800,
            padding: "6px 14px", borderRadius: "999px", letterSpacing: "0.03em",
          }}>
            {met.icon} Entrega: {pedido.metodoEntrega}
          </span>
        )}
        <div style={{ fontSize: "1.8rem", marginTop: "8px" }}>⏳</div>
        <p style={{ margin: 0, color: "#64748b" }}>Iniciando preparación…</p>
      </div>
    );
  }

  // Estado: ya TERMINÉ todas mis partes (preparadas o con problema reportado), pero
  // el pedido sigue EN_PREPARACION porque falta otra categoría (de otro operario).
  // Para mí es un pedido terminado → vista de solo lectura.
  const _prepMap = pedido.partesPreparadas || {};
  const _errMap = pedido.partesConError || {};
  const misPartesTerminadas = tengoParte && misPartes.every(p => _prepMap[p] === true || _errMap[p]);
  const misPartesConProblema = tengoParte && misPartes.some(p => _errMap[p]);
  if (!esIsabela && misPartesTerminadas && pedido.estado === ESTADOS.EN_PREPARACION) {
    const problema = misPartesConProblema;
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
        <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px" }}>
          <VolverListaPedidos to={puedeVincularKits ? "/pedidos" : "/operario/asignados"} />
          <div style={{ marginTop: "10px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
            <div>
              <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                Pedido #{pedido.numero}
              </p>
              <h1 style={{ margin: "2px 0 0", fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                {pedido.cliente}
              </h1>
            </div>
            <span style={{
              flexShrink: 0,
              background: problema ? "#fef2f2" : "#f0fdf4",
              color: problema ? "#b91c1c" : "#15803d",
              fontSize: "0.7rem", fontWeight: 700, padding: "3px 10px", borderRadius: "999px",
              letterSpacing: "0.04em", marginTop: "4px",
              border: `1px solid ${problema ? "#fca5a5" : "#86efac"}`,
            }}>
              {problema ? "⚠️ PROBLEMA REPORTADO" : "✅ TU PARTE LISTA"}
            </span>
          </div>
        </div>

        <div style={{ padding: "16px" }}>
          <div style={{
            background: problema ? "#fef2f2" : "#f0fdf4",
            border: `1.5px solid ${problema ? "#fca5a5" : "#86efac"}`,
            borderRadius: "12px", padding: "14px 16px", marginBottom: "16px", textAlign: "center",
          }}>
            <p style={{ margin: 0, fontWeight: 700, color: problema ? "#b91c1c" : "#15803d", fontSize: "0.95rem" }}>
              {problema ? "Reportaste un problema en tu parte" : "Ya preparaste tu parte"}
            </p>
            <p style={{ margin: "4px 0 0", color: problema ? "#991b1b" : "#166534", fontSize: "0.8rem" }}>
              Falta que se terminen las demás partes del pedido. No tenés que hacer nada más acá.
            </p>
          </div>
        </div>

        <ListaProductosSoloLectura pedido={{ ...pedido, productos: productosLite }} catalogoMap={catalogoMap} />
      </div>
    );
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "100px" }}>

      {/* ── Header ─────────────────────────────── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px" }}>
        {/* ISABELA operario → pipeline; encargado (abre su parte) → tablero; R8 operario → su lista */}
        <VolverListaPedidos to={esIsabela || puedeVincularKits ? "/pedidos" : "/operario/asignados"} />
        <div style={{ marginTop: "10px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
          <div>
            <p style={{ margin: 0, fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Pedido #{pedido.numero}
            </p>
            <h1 style={{ margin: "2px 0 0", fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
              {pedido.cliente}
            </h1>
          </div>
          <span style={{
            flexShrink: 0,
            background: "#fef9c3", color: "#854d0e",
            fontSize: "0.7rem", fontWeight: 700,
            padding: "3px 10px", borderRadius: "999px",
            letterSpacing: "0.04em", marginTop: "4px"
          }}>
            EN PREPARACIÓN
          </span>
        </div>
        {/* Método de entrega (R8): GIRA / CAMION / RETIRA / AGENCIA — bien visible para el operario */}
        {pedido.metodoEntrega && (() => {
          const c = {
            AGENCIA: { bg: "#eff6ff", color: "#1d4ed8", icon: "🏢" },
            RETIRA:  { bg: "#f0fdf4", color: "#15803d", icon: "🤝" },
            CAMION:  { bg: "#fef3c7", color: "#92400e", icon: "🚚" },
            GIRA:    { bg: "#fdf4ff", color: "#7e22ce", icon: "🚌" },
          }[pedido.metodoEntrega] || { bg: "#f1f5f9", color: "#475569", icon: "📦" };
          return (
            <div style={{ marginTop: "8px" }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                background: c.bg, color: c.color,
                fontSize: "0.82rem", fontWeight: 800,
                padding: "5px 12px", borderRadius: "999px",
                letterSpacing: "0.03em",
              }}>
                {c.icon} Entrega: {pedido.metodoEntrega}
              </span>
            </div>
          );
        })()}
        {(pedido.metodoPedido || pedido.deposito) && (
          <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "#94a3b8" }}>
            {[pedido.metodoPedido, pedido.deposito].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>

      {/* ── Aviso de modificaciones del pedido ──── */}
      {modInfo && (modInfo.removedItems || []).length > 0 && (
        <div style={{ margin: "12px 16px 0", background: "#fff7ed", border: "1.5px solid #f97316", borderRadius: "12px", padding: "10px 14px", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>⚠️</span>
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#9a3412", fontWeight: 600 }}>
            Este pedido tuvo modificaciones — algunos productos fueron quitados o cambiaron.
          </p>
        </div>
      )}

      {/* ── Progreso ────────────────────────────── */}
      <div style={{ padding: "16px 16px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span style={{ fontSize: "0.82rem", color: "#475569", fontWeight: 600 }}>
            {checkedCount === totalProductos && totalProductos > 0
              ? "Todos los productos cargados"
              : `${checkedCount} de ${totalProductos} productos cargados`}
          </span>
          <span style={{ fontSize: "0.82rem", fontWeight: 700, color: checkedCount === totalProductos ? "#16a34a" : "#475569" }}>
            {progreso}%
          </span>
        </div>
        <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "999px", overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: "999px",
            background: checkedCount === totalProductos ? "#16a34a" : "#3b82f6",
            width: `${progreso}%`,
            transition: "width 0.3s ease, background 0.3s ease"
          }} />
        </div>
      </div>

      {/* ── Lista de productos ───────────────────── */}
      {lite ? (
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {totalProductos === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 16px", color: "#94a3b8" }}>
              <p style={{ margin: 0 }}>No hay productos en este pedido.</p>
            </div>
          ) : (
            itemsCargables.map((it, i) => {
              const k = it._key;
              const cargado = !!checks[k];
              const qty = reqQty(it);
              const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
              const uru = toURUCode(raw);
              const cat = catalogoMap[uru] || {};
              const nombre = getNombreProducto(it, cat);
              const color = cat?.finish || cat?.color || "";
              const modBadge = getModBadge(uru);
              const esCompDeKit = !!it._kitUru;
              const prev = itemsCargables[i - 1];
              const primeroDelKit = esCompDeKit && (!prev || prev._kitUru !== it._kitUru);

              return (
                <div key={k}>
                {primeroDelKit && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "6px 0 4px", padding: "7px 12px", background: "#f3e8ff", borderRadius: "10px" }}>
                    <span style={{ fontSize: "0.66rem", fontWeight: 800, color: "#7c3aed", background: "#fff", padding: "2px 8px", borderRadius: "999px" }}>🧩 KIT</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: "0.82rem", fontWeight: 700, color: "#6b21a8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it._kitNombre}</span>
                    {it._kitNumero && <span style={{ flexShrink: 0, fontSize: "0.66rem", color: "#7c3aed" }}>ped #{it._kitNumero}</span>}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => toggleCheck(k)}
                  style={{
                    display: "flex", alignItems: "center", gap: "14px",
                    width: "100%", textAlign: "left",
                    background: cargado ? "#f0fdf4" : "#fff",
                    border: `1.5px solid ${cargado ? "#86efac" : esCompDeKit ? "#ddd6fe" : modBadge ? "#fde68a" : "#e2e8f0"}`,
                    borderLeft: esCompDeKit ? "4px solid #a78bfa" : undefined,
                    borderRadius: "14px",
                    padding: "14px 16px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    boxShadow: cargado ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Indicador check */}
                  <div style={{
                    flexShrink: 0,
                    width: "28px", height: "28px",
                    borderRadius: "50%",
                    border: `2px solid ${cargado ? "#16a34a" : "#cbd5e1"}`,
                    background: cargado ? "#16a34a" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.15s ease",
                  }}>
                    {cargado && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2.5 7L5.5 10L11.5 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>

                  {/* Info producto */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <p style={{
                        margin: 0,
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: cargado ? "#15803d" : "#1e293b",
                        lineHeight: 1.35,
                        textDecoration: cargado ? "line-through" : "none",
                        wordBreak: "break-word",
                      }}>
                        {nombre}
                      </p>
                      {modBadge && (
                        <span style={{
                          fontSize: "0.62rem", fontWeight: 800,
                          padding: "2px 7px", borderRadius: 999,
                          background: modBadge.bg, color: modBadge.color,
                          letterSpacing: "0.06em", textTransform: "uppercase", flexShrink: 0,
                        }}>
                          {modBadge.label}
                        </span>
                      )}
                    </div>
                    {color && (
                      <span style={{ display: "inline-block", marginTop: "5px", fontSize: "0.74rem", fontWeight: 800, color: "#0f172a", background: "#e2e8f0", padding: "2px 10px", borderRadius: "6px", textTransform: "capitalize", letterSpacing: "0.02em" }}>{color}</span>
                    )}
                  </div>

                  {/* Cantidad (con overlay de modificación si corresponde) */}
                  {(() => {
                    const mods = Array.isArray(pedido?.modificaciones) ? pedido.modificaciones : [];
                    const codm = String(it?.cod || it?.codigo || "").trim();
                    const m = codm ? [...mods].reverse().find(x => String(x?.cod || "").trim() === codm) : null;
                    const amber = { flexShrink: 0, background: "#fef3c7", color: "#92400e", fontWeight: 700, fontSize: "0.85rem", padding: "4px 8px", borderRadius: "8px", display: "inline-flex", alignItems: "center", gap: "4px" };
                    if (m && m.cantReal != null && m.cantPedida != null) {
                      if (editMod === codm) {
                        const guardar = async () => { const ok = await editarModificacionOperario(codm, editVal); if (ok) setEditMod(null); };
                        return (
                          <div onClick={e => e.stopPropagation()} style={amber}>
                            <s style={{ textDecoration: "line-through", opacity: 0.65 }}>{m.cantPedida}</s>→
                            <input autoFocus type="number" min="0" max={Math.max(0, Number(m.cantPedida) - 1)} value={editVal}
                              onChange={e => setEditVal(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") guardar(); else if (e.key === "Escape") setEditMod(null); }}
                              style={{ width: 42, textAlign: "center", border: "1.5px solid #f59e0b", borderRadius: 6, padding: "2px 3px", fontSize: "0.82rem", fontWeight: 700, color: "#92400e", fontFamily: "inherit" }} />
                            <button type="button" onClick={(e) => { e.stopPropagation(); guardar(); }} style={{ border: "none", background: "#15803d", color: "#fff", borderRadius: 6, padding: "2px 7px", fontWeight: 700, cursor: "pointer" }}>✓</button>
                          </div>
                        );
                      }
                      return (
                        <div onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditMod(codm); setEditVal(m.cantReal != null ? String(m.cantReal) : ""); }} title="Tocá para editar la cantidad cargada" style={{ ...amber, cursor: "pointer" }}>
                          <s style={{ textDecoration: "line-through", opacity: 0.65 }}>{m.cantPedida}</s>→<strong>{m.cantReal}</strong>✏️
                        </div>
                      );
                    }
                    return (
                      <div style={{ flexShrink: 0, background: cargado ? "#dcfce7" : "#f1f5f9", color: cargado ? "#15803d" : "#475569", fontWeight: 700, fontSize: "0.85rem", padding: "4px 10px", borderRadius: "8px" }}>
                        ×{qty}
                      </div>
                    );
                  })()}
                </button>
                {esKitPredefinido(uru) && <KitComponentesFijos codigoKit={uru} cantKit={qty} />}
                </div>
              );
            })
          )}

          {/* Marcar todo (solo si quedan pendientes) */}
          {totalProductos > 0 && !allChecked && (
            <button
              type="button"
              onClick={marcarTodo}
              style={{
                width: "100%", padding: "10px",
                background: "transparent", border: "1.5px dashed #cbd5e1",
                borderRadius: "12px", color: "#64748b",
                fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              Marcar todos como cargados
            </button>
          )}

          {/* ── Problema activo ── */}
          {pedido?.problema?.estado === "PENDIENTE" && (
            <div style={{ background: "#fff7ed", border: "1.5px solid #fb923c", borderRadius: "12px", padding: "12px 14px", display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: "0.84rem", color: "#9a3412", fontWeight: 600 }}>
                Problema reportado — esperando al encargado
              </p>
            </div>
          )}

          {/* ── Reportar problema ── */}
          {!pedido?.problema || pedido?.problema?.estado === "REVISADO" ? (
            <button
              type="button"
              onClick={() => {
                setProblemaForm({ productoIdxs: [], tipo: "FALTA_STOCK", desc: "", cantidades: {} });
                setShowProblema(true);
              }}
              style={{
                width: "100%", padding: "10px",
                background: "transparent", border: "1.5px dashed #fca5a5",
                borderRadius: "12px", color: "#ef4444",
                fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              ⚠️ Reportar un problema con el pedido
            </button>
          ) : null}
        </div>
      ) : (
        /* ── Modo escáner (sin cambios) ── */
        <div style={{ padding: "12px 16px" }}>
          <div className="space-y-2">
            {productosOperario.map((it, i) => {
              const k = keyFor(it, i);
              const need = reqQty(it);
              const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
              const uru = toURUCode(raw);
              const cat = catalogoMap[uru] || {};
              const customer = cat?.customerNo || cat?.customer_no || "—";
              const color = cat?.finish || cat?.color || "—";
              const p = prep[k] || { stock: 0, usarSueltas: 0, paquete: null, usarDePaquete: 0 };
              const usados = (p.usarSueltas || 0) + (p.usarDePaquete || 0);
              const ok = usados >= need && need > 0;

              const setP = (changes) =>
                setPrep(prev => ({ ...prev, [k]: { ...(prev[k] || {}), ...changes } }));

              return (
                <div key={k} className="card product-card">
                  <div className="product-card__head">
                    <div className="product-card__title">
                      <span className="product-card__sku">{customer}</span>
                      <span className="product-card__color">{color}</span>
                    </div>
                    <div className="product-card__chips">
                      <span className="chip chip--muted">Req: {need}</span>
                      <span className={`chip ${ok ? "chip--ok" : ""}`}>Usado: {usados}</span>
                    </div>
                  </div>
                  <div className="product-card__meta">
                    <span className="meta-pill">Sueltas: <strong>{Number(p.stock || 0)}</strong></span>
                    <span className="meta-pill">Pack: <strong>{p.paquete?.packSize ? `x${p.paquete.packSize}` : "—"}</strong></span>
                  </div>
                  <div className="qty-row">
                    <span className="qty-row__label">Sueltas</span>
                    <div className="qty">
                      <button type="button" className="qty__btn"
                        onClick={() => setPrep(prev => ({ ...prev, [k]: clampPrepForNeed(k, { usarSueltas: Math.max(0, Number((prev[k] || {}).usarSueltas || 0) - 1) }) }))}
                        disabled={Number(p.usarSueltas || 0) <= 0}>−</button>
                      <span className="qty__val">{Number(p.usarSueltas || 0)}</span>
                      <button type="button" className="qty__btn"
                        onClick={() => setPrep(prev => { const cur = prev[k] || {}; const usedNow = Number(cur.usarSueltas || 0) + Number(cur.usarDePaquete || 0); const remaining = Math.max(0, need - usedNow); const stockLeft = Number(cur.stock || 0) - Number(cur.usarSueltas || 0); const step = Math.min(remaining, stockLeft); return { ...prev, [k]: clampPrepForNeed(k, { usarSueltas: Number(cur.usarSueltas || 0) + (step > 0 ? 1 : 0) }) }; })}
                        disabled={(() => { const usedNow = Number(p.usarSueltas || 0) + Number(p.usarDePaquete || 0); const remaining = Math.max(0, need - usedNow); const stockLeft = Number(p.stock || 0) - Number(p.usarSueltas || 0); return remaining <= 0 || stockLeft <= 0; })()}>+</button>
                    </div>
                  </div>
                  {p.paquete ? (
                    <div className="qty-row">
                      <span className="qty-row__label">Paquete</span>
                      <div className="qty">
                        <button type="button" className="qty__btn"
                          onClick={() => setPrep(prev => { const cur = prev[k] || {}; return { ...prev, [k]: { ...cur, usarDePaquete: Math.max(0, Number(cur.usarDePaquete || 0) - 1) } }; })}
                          disabled={Number(p.usarDePaquete || 0) <= 0}>−</button>
                        <span className="qty__val">{Number(p.usarDePaquete || 0)}</span>
                        <button type="button" className="qty__btn"
                          onClick={() => setPrep(prev => { const cur = prev[k] || {}; const usedNow = Number(cur.usarSueltas || 0) + Number(cur.usarDePaquete || 0); const remaining = Math.max(0, need - usedNow); const packSize = Number(cur.paquete?.packSize || 0); const roomInPack = Math.max(0, packSize - Number(cur.usarDePaquete || 0)); const stepAllowed = Math.min(remaining, roomInPack); return { ...prev, [k]: { ...cur, usarDePaquete: Number(cur.usarDePaquete || 0) + (stepAllowed > 0 ? 1 : 0) } }; })}
                          disabled={(() => { const usedNow = Number(p.usarSueltas || 0) + Number(p.usarDePaquete || 0); const remaining = Math.max(0, need - usedNow); const packSize = Number(p.paquete?.packSize || 0); const roomInPack = Math.max(0, packSize - Number(p.usarDePaquete || 0)); return remaining <= 0 || roomInPack <= 0; })()}>+</button>
                      </div>
                    </div>
                  ) : (
                    <div className="qty-row qty-row--hint">
                      <span className="qty-row__label">Paquete</span>
                      <span className="hint">—</span>
                    </div>
                  )}
                  <button type="button" className="btn btn--secondary btn--full mt-2"
                    onClick={() => setScanForKey(String(k))}>
                    Escanear paquete
                  </button>
                </div>
              );
            })}
          </div>
          <button type="button"
            className="btn w-full bg-black text-white disabled:opacity-60"
            style={{ marginTop: "16px" }}
            onClick={finalizarPreparacion}
            disabled={saving || !(pedido?.productos || []).every((it, i) => {
              if (esEncabezadoKit(it)) return true; // el encabezado del kit no se prepara
              const k = keyFor(it, i);
              const p = prep[k] || {};
              return (Number(p.usarSueltas || 0) + Number(p.usarDePaquete || 0)) >= reqQty(it);
            })}>
            {saving ? "Guardando…" : "Finalizar preparación"}
          </button>
        </div>
      )}

      {/* ── CTA fijo abajo (solo modo lite) ────── */}
      {lite && (
        <div className="cta-fija" style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          padding: "12px 16px 20px",
          background: "#fff",
          borderTop: "1px solid #e2e8f0",
          boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
        }}>
          <button
            type="button"
            onClick={finalizarPreparacion}
            disabled={!allChecked || saving}
            style={{
              width: "100%",
              padding: "15px",
              borderRadius: "14px",
              border: "none",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: allChecked ? "pointer" : "not-allowed",
              transition: "all 0.2s ease",
              background: allChecked ? "#16a34a" : "#e2e8f0",
              color: allChecked ? "#fff" : "#94a3b8",
            }}
          >
            {saving
              ? "Guardando…"
              : allChecked
              ? "✓ Marcar pedido como preparado"
              : `Faltan ${totalProductos - checkedCount} producto${totalProductos - checkedCount !== 1 ? "s" : ""}`}
          </button>
        </div>
      )}

      {/* ── Overlay: Reportar problema ── */}
      {showProblema && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "flex-end" }}>
          <div style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: "24px 16px 40px", width: "100%", maxHeight: "92vh", overflow: "auto" }}>
            {/* Título */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, fontWeight: 700, fontSize: "1.1rem", color: "#0f172a" }}>Reportar problema</h2>
              <button
                type="button"
                onClick={() => setShowProblema(false)}
                style={{ background: "none", border: "none", fontSize: "1.2rem", color: "#94a3b8", cursor: "pointer", padding: "4px 8px" }}
              >
                ✕
              </button>
            </div>

            {/* Seleccionar producto(s) — se pueden marcar varios */}
            <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              ¿En qué producto(s)? <span style={{ fontWeight: 500, textTransform: "none" }}>— podés marcar varios</span>
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "7px", marginBottom: "20px" }}>
              {(pedido?.productos || [])
                // Solo los productos de MIS partes (si el pedido está dividido).
                .map((it, i) => ({ it, i }))
                .filter(({ it }) => {
                  if (!tengoParte) return true;
                  const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
                  const uru = toURUCode(raw);
                  return misPartes.includes(parteDeProducto(uru, catalogoMap?.[uru], it));
                })
                .map(({ it, i }) => {
                const raw = it.cod || it.codigo || it.codigoURU || it.desc || it.descripcion || it.nombre || "";
                const uru = toURUCode(raw);
                const cat = catalogoMap[uru] || {};
                const nombre = getNombreProducto(it, cat);
                const color = cat?.finish || cat?.color || "";
                // Fallback: descripción de Finnegans (trae el color) si el catálogo no tiene color.
                const descFin = fixEncoding(it.desc || it.descripcion || "");
                const qty = reqQty(it);
                const selected = problemaForm.productoIdxs.includes(i);
                return (
                  <div key={i}>
                  <button
                    type="button"
                    onClick={() => setProblemaForm(p => ({
                      ...p,
                      productoIdxs: p.productoIdxs.includes(i)
                        ? p.productoIdxs.filter(x => x !== i)
                        : [...p.productoIdxs, i],
                    }))}
                    style={{
                      display: "flex", alignItems: "center", gap: "10px",
                      padding: "11px 14px", textAlign: "left", width: "100%",
                      background: selected ? "#eff6ff" : "#f8fafc",
                      border: `1.5px solid ${selected ? "#3b82f6" : "#e2e8f0"}`,
                      borderRadius: "12px", cursor: "pointer",
                      fontWeight: selected ? 600 : 400, fontSize: "0.88rem",
                      color: selected ? "#1d4ed8" : "#1e293b",
                      transition: "all 0.1s ease",
                    }}
                  >
                    {/* Checkbox */}
                    <span style={{
                      flexShrink: 0, width: "22px", height: "22px", borderRadius: "6px",
                      border: `2px solid ${selected ? "#3b82f6" : "#cbd5e1"}`,
                      background: selected ? "#3b82f6" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: "0.8rem", fontWeight: 800,
                    }}>{selected ? "✓" : ""}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: "0.85rem", lineHeight: 1.35, wordBreak: "break-word" }}>{nombre}</span>
                      {color ? (
                        <span style={{ display: "inline-block", marginTop: "4px", fontSize: "0.72rem", fontWeight: 800, color: "#0f172a", background: "#e2e8f0", padding: "1px 9px", borderRadius: "6px", textTransform: "capitalize" }}>{color}</span>
                      ) : (descFin && descFin.toLowerCase() !== nombre.toLowerCase() ? (
                        <span style={{ display: "block", marginTop: "3px", fontSize: "0.72rem", color: "#94a3b8", wordBreak: "break-word" }}>{descFin}</span>
                      ) : null)}
                    </span>
                    <span style={{ flexShrink: 0, marginLeft: "8px", background: selected ? "#dbeafe" : "#f1f5f9", color: selected ? "#1d4ed8" : "#64748b", fontWeight: 700, fontSize: "0.78rem", padding: "2px 7px", borderRadius: "6px" }}>
                      ×{qty}
                    </span>
                  </button>
                  {/* Falta de stock: pedir cuánto stock real hay disponible */}
                  {selected && problemaForm.tipo === "FALTA_STOCK" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 6px 4px 46px" }}>
                      <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#b91c1c" }}>
                        Cuánto cargaste (menos de {qty}):
                      </label>
                      <input
                        type="number" min="0" max={Math.max(0, Number(qty) - 1)} inputMode="numeric"
                        value={problemaForm.cantidades?.[i] ?? ""}
                        onChange={e => setProblemaForm(p => ({ ...p, cantidades: { ...(p.cantidades || {}), [i]: e.target.value } }))}
                        placeholder="0"
                        style={{ width: "76px", padding: "7px 8px", border: "1.5px solid #fca5a5", borderRadius: "8px", fontSize: "0.9rem", textAlign: "center", color: "#0f172a" }}
                      />
                    </div>
                  )}
                  </div>
                );
              })}
            </div>

            {/* Tipo de problema */}
            <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              ¿Cuál es el problema?
            </p>
            <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
              {[["FALTA_STOCK", "📦 Falta de stock"], ["OTRO", "⚠️ Otro motivo"]].map(([val, label]) => {
                const sel = problemaForm.tipo === val;
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setProblemaForm(p => ({ ...p, tipo: val }))}
                    style={{
                      flex: 1, padding: "12px 8px",
                      background: sel ? "#fef2f2" : "#f8fafc",
                      border: `1.5px solid ${sel ? "#f87171" : "#e2e8f0"}`,
                      borderRadius: "12px", cursor: "pointer",
                      fontWeight: 600, fontSize: "0.85rem",
                      color: sel ? "#dc2626" : "#475569",
                      transition: "all 0.1s ease",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Descripción */}
            <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Descripción (opcional)
            </p>
            <textarea
              value={problemaForm.desc}
              onChange={e => setProblemaForm(p => ({ ...p, desc: e.target.value }))}
              placeholder={problemaForm.tipo === "FALTA_STOCK"
                ? "Ej: Solo hay 3 y necesito 5…"
                : "Describí el problema brevemente…"}
              rows={3}
              style={{
                width: "100%", padding: "12px",
                border: "1.5px solid #e2e8f0", borderRadius: "12px",
                fontSize: "0.9rem", resize: "none",
                boxSizing: "border-box", marginBottom: "16px",
              }}
            />

            {/* CTA — dos caminos */}
            <p style={{ margin: "0 0 10px", fontSize: "0.78rem", color: "#64748b", lineHeight: 1.4 }}>
              Si cargaste lo que había y el pedido puede seguir, usá <strong>"Seguir"</strong> (el encargado lo confirma al controlar). Si no se puede continuar, <strong>"Frenar"</strong> lo manda a revisión.
            </p>
            <button
              type="button"
              onClick={registrarModificacion}
              disabled={reportando}
              style={{
                width: "100%", padding: "15px", borderRadius: "14px", border: "none",
                background: "#15803d", color: "#fff",
                fontWeight: 700, fontSize: "1rem", cursor: "pointer", marginBottom: "10px",
              }}
            >
              {reportando ? "Guardando…" : "✅ Seguir — cargué lo disponible"}
            </button>
            <button
              type="button"
              onClick={reportarProblema}
              disabled={reportando}
              style={{
                width: "100%", padding: "14px", borderRadius: "14px",
                border: "1.5px solid #fca5a5", background: "#fff", color: "#dc2626",
                fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
              }}
            >
              {reportando ? "Reportando…" : "🛑 Frenar el pedido (error)"}
            </button>
          </div>
        </div>
      )}

      {/* ── Overlay escáner (solo modo completo) ── */}
      {!lite && overlayOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.62)",
          zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
        }}>
          <div className="card" style={{
            width: "100%", maxWidth: 420, maxHeight: "92vh", overflow: "auto",
            background: "#fff", borderRadius: 12,
            boxShadow: "0 20px 50px rgba(0,0,0,.35)",
          }}>
            <div className="subsection-title" style={{ position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
              <span>{scanForKey ? "Escanear paquete" : packError ? "Paquete incorrecto" : "Confirmar paquete"}</span>
              <button type="button" className="btn btn--ghost"
                onClick={() => { setScanForKey(null); setPackError(null); setPackConfirm(null); }}>
                Cerrar
              </button>
            </div>
            <div className="p-3">
              {scanForKey && !packConfirm && !packError && (
                <>
                  <QrScanner
                    key={scanForKey}
                    onScanSuccess={(payload) => onScanPaquete(payload)}
                    onError={(err) => { console.error(err); toast?.error?.("Error con la cámara"); setScanForKey(null); }}
                  />
                  <p className="muted text-center mt-2" style={{ fontSize: "0.8rem" }}>
                    Apuntá la cámara al código QR del paquete
                  </p>
                </>
              )}
              {packError && (
                <PackErrorModal
                  esperado={packError.esperado}
                  qr={packError.qr}
                  onRetry={() => { setPackError(null); setScanForKey(scanForKey); }}
                  onClose={() => { setPackError(null); setScanForKey(null); }}
                />
              )}
              {packConfirm && (
                <PackConfirmModal
                  {...packConfirm}
                  onConfirm={(qty) => {
                    const k = packConfirm.key;
                    setPrep(prev => {
                      const cur = prev[k] || {};
                      return { ...prev, [k]: clampPrepForNeed(k, { paquete: { packSize: packConfirm.packSize }, usarDePaquete: qty }) };
                    });
                    setPackConfirm(null);
                  }}
                  onClose={() => setPackConfirm(null)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
